import React, { useState, useRef, useEffect } from 'react';
import Icon from './AppIcon';

const AudioPlayer = ({ 
  audioUrl, 
  label = 'Play Audio', 
  size = 'md',
  variant = 'default',
  onPlayStart,
  onPlayEnd,
  showDuration = true,
  autoPlay = false,
  className = ''
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!audioUrl) {
      setError('No audio available');
      return;
    }

    const audio = audioRef?.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio?.duration || 0);
      setIsLoading(false);
      if (autoPlay) {
        handlePlay();
      }
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio?.currentTime || 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onPlayEnd?.();
    };

    const handleError = (e) => {
      console.error('Audio playback error:', e);
      setError('Failed to load audio');
      setIsLoading(false);
      setIsPlaying(false);
    };

    audio?.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio?.addEventListener('timeupdate', handleTimeUpdate);
    audio?.addEventListener('ended', handleEnded);
    audio?.addEventListener('error', handleError);

    return () => {
      audio?.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio?.removeEventListener('timeupdate', handleTimeUpdate);
      audio?.removeEventListener('ended', handleEnded);
      audio?.removeEventListener('error', handleError);
    };
  }, [audioUrl, autoPlay, onPlayEnd]);

  const handlePlay = async () => {
    const audio = audioRef?.current;
    if (!audio) return;

    try {
      setIsLoading(true);
      setError(null);
      
      if (isPlaying) {
        audio?.pause();
        setIsPlaying(false);
      } else {
        await audio?.play();
        setIsPlaying(true);
        onPlayStart?.();
      }
    } catch (err) {
      console.error('Error playing audio:', err);
      setError('Playback failed');
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeek = (e) => {
    const audio = audioRef?.current;
    if (!audio) return;

    const rect = e?.currentTarget?.getBoundingClientRect();
    const x = e?.clientX - rect?.left;
    const percentage = x / rect?.width;
    const newTime = percentage * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'h-8 px-3 text-sm';
      case 'lg':
        return 'h-12 px-6 text-lg';
      default:
        return 'h-10 px-4 text-base';
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'ghost':
        return 'bg-transparent hover:bg-primary/10 text-primary';
      case 'outline':
        return 'bg-transparent border border-primary text-primary hover:bg-primary/10';
      case 'minimal':
        return 'bg-transparent text-foreground hover:text-primary';
      default:
        return 'bg-primary text-primary-foreground hover:bg-primary/90';
    }
  };

  if (!audioUrl) {
    return null;
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          disabled={isLoading || error}
          className={`
            inline-flex items-center justify-center gap-2 rounded-lg font-medium
            transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
            ${getSizeClasses()} ${getVariantClasses()}
          `}
          title={error || label}
        >
          {isLoading ? (
            <>
              <Icon name="Loader2" size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} className="animate-spin" />
              <span>Loading...</span>
            </>
          ) : error ? (
            <>
              <Icon name="AlertCircle" size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />
              <span>Error</span>
            </>
          ) : isPlaying ? (
            <>
              <Icon name="Pause" size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />
              <span>Pause</span>
            </>
          ) : (
            <>
              <Icon name="Play" size={size === 'sm' ? 16 : size === 'lg' ? 24 : 20} />
              <span>{label}</span>
            </>
          )}
        </button>

        {showDuration && duration > 0 && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {formatTime(currentTime)}
            </span>
            
            <div 
              className="flex-1 h-2 bg-muted rounded-full cursor-pointer relative overflow-hidden group"
              onClick={handleSeek}
            >
              <div 
                className="absolute top-0 left-0 h-full bg-primary transition-all duration-100"
                style={{ width: `${(currentTime / duration) * 100}%` }}
              />
              <div className="absolute top-0 left-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity bg-primary/10" />
            </div>
            
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {formatTime(duration)}
            </span>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
};

export default AudioPlayer;